import * as yup from 'yup'
import { linkValidation } from 'components/Project/IsaacTable'
import { IArtist } from 'shared/interfaces'
import { AnyObject } from 'yup/es/types'

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueName(message: string, mapper?: (value: T, index?: number, list?: T[]) => T[]): ArraySchema<T>
    uniqueProduct(message: string, mapper?: (value: T, index?: number, list?: T[]) => T[]): ArraySchema<T>
  }
}

interface IAsyncProductSchema {
  checkArtistName: (name: string) => Promise<IArtist | undefined>
}

export const uniqueName = function (list: any[] = [], { createError, path }: AnyObject) {
  for (let i = 0; i < list.length; i++) {
    if (list.find((el: any, idx) => el.name === list[i].name && idx !== i && el.isSaved)) {
      return createError({
        path: `${path}[${i}].name`,
        message: 'Product has already been taken.'
      })
    }
  }
  return true
}

export const uniqueProduct = function (list: any[] = [], { parent, createError, path }: AnyObject) {
  for (let i = 0; i < list.length; i++) {
    if (!list[i].isSaved) {
      const parentProduct = parent?.artistProducts?.find(
        (product: any) => product.name === list[i].name && product.configuration === list[i].configuration
      )
      const productInZone = parentProduct?.locals.find((local: any) => local.label_id === parent.labelId)

      if (productInZone) {
        return createError({
          path: `${path}[${i}].name`,
          message: `Product is used in project with id ${productInZone.campaign_id}`
        })
      }
    }
  }
  return true
}

export const productSchema = ({ checkArtistName }: IAsyncProductSchema) => {
  yup.addMethod(yup.array, 'uniqueName', function (message) {
    return this.test('uniqueName', message, uniqueName)
  })

  yup.addMethod(yup.array, 'uniqueProduct', function (message) {
    return this.test('uniqueProduct', message, uniqueProduct)
  })

  return yup.object().shape({
    isaacUrl: linkValidation,
    products: yup
      .array()
      .when('step', {
        is: (value: number) => value > 1,
        then: yup
          .array()
          .of(
            yup.object().shape({
              name: yup.string().required('Product Name is required.'),
              configuration: yup.string().required('Type is required.'),
              publish_date: yup.string().required('Release Date is required.')
            })
          )
          .when('artistProducts', (artistProducts: any, schema: any) => {
            return schema.max(5)
          })
      })
      .when('step', {
        is: (value: number) => value > 1,
        then: yup.array().uniqueName('Product has already been taken.').uniqueProduct('Product is used in project')
      }),
    artistName: yup
      .string()
      .when('step', {
        is: (value: number) => value === 0,
        then: yup.string().required('Artist Name is required.')
      })
      .when('artistId', {
        is: (value: string) => !value,
        then: yup.string().test('artistName', 'Artist Name has already been taken', async (name) => {
          if (typeof name === 'string') {
            try {
              const existingArtist = await checkArtistName(name)

              return !existingArtist
            } catch {
              return false
            }
          }
          return false
        })
      }),
    unit: yup.string().when('step', {
      is: (value: number) => value > 0,
      then: yup.string().required('Unit is required.')
    }),
    projectName: yup.string().when('step', {
      is: (value: number) => value > 0,
      then: yup.string().required('Project Name is required.')
    })
  })
}
